import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Select, Button, Input, Spin, message } from 'antd';
import { LeftOutlined, WarningOutlined, AppstoreOutlined, CameraOutlined } from '@ant-design/icons';
import api from '../api';
import { getStoredUser, hasPerm, isSuperAdmin as checkSuperAdmin } from '../userAuth';
import { H5_TAB_BAR_PX } from '../h5Layout';

const scrapReasons = [
  { value: '设备老化', label: '设备老化' },
  { value: '功能故障', label: '功能故障' },
  { value: '技术淘汰', label: '技术淘汰' },
  { value: '其他', label: '其他' },
];

export default function ApplyScrap() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = getStoredUser();
  const [asset, setAsset] = useState(null);
  const [reason, setReason] = useState(null);
  const [detail, setDetail] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const scrapEligible = Number(asset?.net_value || 0) === 0;

  useEffect(() => {
    if (checkSuperAdmin(user)) {
      message.info('超级管理员请在「处置资产」中心提交未上报');
      navigate(-1);
      return;
    }
    api.get(`/assets/${id}`)
      .then(res => setAsset(res))
      .catch(() => message.error('加载失败'))
      .finally(() => setLoading(false));
  }, [id, navigate, user]);

  const handleSubmit = async () => {
    if (!scrapEligible) { message.warning('当前资产未完成折旧（净值不为零），不能提交处置申请'); return; }
    if (!reason) { message.warning('请选择处置原因'); return; }
    if (!detail.trim()) { message.warning('请填写详细说明'); return; }
    setSubmitting(true);
    try {
      await api.post('/applies', {
        apply_type: 3,
        asset_ids: [Number(id)],
        remark: `${reason}: ${detail}`,
      });
      message.success('处置申请已提交');
      navigate(-1);
    } catch (err) {
      message.error(err.message || '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  const usedYears = asset?.used_month ? (asset.used_month / 12).toFixed(1) : '-';

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f7fa',
      paddingBottom: `calc(${H5_TAB_BAR_PX}px + 88px + env(safe-area-inset-bottom, 0px))`,
    }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', background: '#fff', borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, zIndex: 10 }}>
        <LeftOutlined onClick={() => navigate(-1)} style={{ fontSize: 18, marginRight: 12 }} />
        <span style={{ fontSize: 17, fontWeight: 600 }}>申请处置</span>
      </div>

      <Spin spinning={loading}>
        {asset && (
          <div style={{ padding: 16 }}>
            {/* Asset info card */}
            <div style={{ background: '#fff', borderRadius: 10, padding: 16, marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: '#fff7e6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AppstoreOutlined style={{ fontSize: 22, color: '#fa8c16' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#333' }}>{asset.asset_name}</div>
                  <div style={{ fontSize: 13, color: '#999', marginTop: 2 }}>{asset.asset_no || asset.asset_code}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1, background: '#f5f7fa', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>当前净值</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#fa8c16' }}>
                    ¥{asset.net_value != null ? Number(asset.net_value).toLocaleString() : '-'}
                  </div>
                </div>
                <div style={{ flex: 1, background: '#f5f7fa', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>已使用年限</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#333' }}>{usedYears}年</div>
                </div>
              </div>
            </div>

            {/* Scrap reason */}
            <div style={{ background: '#fff', borderRadius: 10, padding: 16, marginBottom: 12 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#333', marginBottom: 12, paddingLeft: 8, borderLeft: '3px solid #fa8c16' }}>处置原因</div>
              <Select
                placeholder="请选择处置原因"
                style={{ width: '100%' }}
                value={reason}
                onChange={setReason}
                options={scrapReasons}
              />
            </div>

            {/* Detail */}
            <div style={{ background: '#fff', borderRadius: 10, padding: 16, marginBottom: 12 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#333', marginBottom: 12, paddingLeft: 8, borderLeft: '3px solid #fa8c16' }}>详细说明</div>
              <Input.TextArea
                rows={4}
                placeholder="请详细说明处置原因、资产现状等信息"
                value={detail}
                onChange={e => setDetail(e.target.value)}
                style={{ borderRadius: 8 }}
              />
            </div>

            {/* Upload placeholder */}
            <div style={{ background: '#fff', borderRadius: 10, padding: 16, marginBottom: 12 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#333', marginBottom: 12, paddingLeft: 8, borderLeft: '3px solid #fa8c16' }}>上传处置凭证照片（选填）</div>
              <div style={{
                border: '1px dashed #d9d9d9', borderRadius: 8, padding: '24px 0', textAlign: 'center',
                color: '#999', background: '#fafafa',
              }}>
                <CameraOutlined style={{ fontSize: 28, color: '#bbb', marginBottom: 8 }} />
                <div style={{ fontSize: 13 }}>点击上传照片</div>
              </div>
            </div>

            {/* Warning */}
            <div style={{ background: '#fff7e6', borderRadius: 8, padding: '10px 12px', display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12, border: '1px solid #ffe7ba' }}>
              <WarningOutlined style={{ color: '#fa8c16', marginTop: 2, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#d46b08', lineHeight: 1.5 }}>
                处置申请审核通过后进入「未上报」，由超级管理员继续上报与处置流程。
              </span>
            </div>
            {!scrapEligible && (
              <div style={{ background: '#fff1f0', borderRadius: 8, padding: '10px 12px', marginBottom: 12, border: '1px solid #ffa39e', fontSize: 13, color: '#cf1322' }}>
                当前资产未完成折旧（净值不为零），不能提交处置申请。
              </div>
            )}
          </div>
        )}
      </Spin>

      {/* Submit button */}
      <div style={{
        position: 'fixed',
        bottom: H5_TAB_BAR_PX,
        left: 0,
        right: 0,
        padding: '12px 16px',
        paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
        background: '#fff',
        borderTop: '1px solid #f0f0f0',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
        zIndex: 90,
      }}
      >
        <Button
          type="primary"
          block
          size="large"
          loading={submitting}
          disabled={!scrapEligible}
          onClick={handleSubmit}
          style={{ borderRadius: 8, height: 46, fontSize: 16, fontWeight: 500, background: '#fa8c16', borderColor: '#fa8c16' }}
        >
          提交处置申请
        </Button>
      </div>
    </div>
  );
}
